import { GetServerSidePropsContext } from 'next';
import { MEN_UNIVERSE, WOMEN_UNIVERSE } from '@constants/user-shopping-preferences';
import { DeviceType } from '@enums/deviceType';
import { isCatalogOrCampaignPage, isHomePage } from '@helpers/routing';
import { getVisitorIdentityFromRequest } from '@helpers/utils/visitor-identity';
import { StickyNavEligibilityParams } from '@interfaces/models/sticky-nav';

export const STICKY_NAV_PARAMS = {
  FROM_STICKY_NAV: 'fromStickyNav',
  STICKY_CATEGORY_ID: 'stickyCategoryId',
  STICKY_UNIVERSE: 'stickyUniverse',
} as const;

type QueryValue = string | string[] | undefined | null;

const normalizeQueryValueToString = (queryValue: QueryValue): string | undefined =>
  queryValue == null ? undefined : Array.isArray(queryValue) ? queryValue[0] : queryValue;

export const isFromStickyNav = (query: Record<string, QueryValue>): boolean =>
  normalizeQueryValueToString(query[STICKY_NAV_PARAMS.FROM_STICKY_NAV]) === '1';

export const getStickyCategoryId = (query: Record<string, QueryValue>): string | undefined =>
  normalizeQueryValueToString(query[STICKY_NAV_PARAMS.STICKY_CATEGORY_ID]) || undefined;

export const getStickyCategoryIdFromParams = (params: URLSearchParams): string | null =>
  params.get(STICKY_NAV_PARAMS.STICKY_CATEGORY_ID);

export const getStickyUniverse = (
  query: Record<string, QueryValue>,
): typeof MEN_UNIVERSE | typeof WOMEN_UNIVERSE | undefined => {
  const stickyUniverseParam = normalizeQueryValueToString(query[STICKY_NAV_PARAMS.STICKY_UNIVERSE]);
  if (stickyUniverseParam === String(WOMEN_UNIVERSE)) {
    return WOMEN_UNIVERSE;
  }
  if (stickyUniverseParam === String(MEN_UNIVERSE)) {
    return MEN_UNIVERSE;
  }
  return undefined;
};

export const appendStickyNavParams = (
  params: URLSearchParams,
  options: { categoryId?: string; universe?: number },
): void => {
  params.set(STICKY_NAV_PARAMS.FROM_STICKY_NAV, '1');
  if (options.categoryId) {
    params.set(STICKY_NAV_PARAMS.STICKY_CATEGORY_ID, options.categoryId);
  }
  if (options.universe === MEN_UNIVERSE || options.universe === WOMEN_UNIVERSE) {
    params.set(STICKY_NAV_PARAMS.STICKY_UNIVERSE, String(options.universe));
  }
};

export const extractStickyNavParamsFromSSR = (
  context: GetServerSidePropsContext,
  initialProps: { deviceType?: string },
): StickyNavEligibilityParams => {
  const isMobile = initialProps.deviceType === DeviceType.ANDROID || initialProps.deviceType === DeviceType.IOS;
  const resolvedUrl = context.resolvedUrl || context.req?.url || '';
  const pathname = resolvedUrl.split('?')[0];
  const query = context.query || {};
  const { isVestiaireReferrer } = getVisitorIdentityFromRequest(context.req);

  return {
    isMobile,
    pathname,
    asPath: resolvedUrl,
    hasFromStickyNav: isFromStickyNav(query),
    isVestiaireReferrer,
  };
};

export const isStickyNavEligibleByPage = (params: StickyNavEligibilityParams): boolean => {
  const { isMobile, pathname, asPath, hasFromStickyNav, isVestiaireReferrer } = params;

  if (!isMobile) {
    return false;
  }
  if (isHomePage(pathname, asPath ?? pathname)) {
    return true;
  }

  return isCatalogOrCampaignPage(pathname) && hasFromStickyNav && isVestiaireReferrer;
};

export const isStickyNavVariant = (testGroup?: string): boolean => !!testGroup && testGroup !== 'control';
